if (!customElements.get('quick-view-modal')) {
  class QuickViewModal extends HTMLElement {
    constructor() {
      super();
      this.toggleState = false;

      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', () => this.init());
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.dialog = this.querySelector('.quick-view-modal__dialog');
      this.main = this.querySelector('.quick-view-modal__main');
      this.loading = this.querySelector('.quick-view-modal__loading');
      this.backdrop = this.querySelector('.quick-view-modal__backdrop');
      this.closeButton = this.querySelector('.quick-view-modal__close');

      this.closeButton?.addEventListener('click', () => this.close());
      this.backdrop?.addEventListener('click', () => this.close());

      this.dialog?.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.toggleState) {
          this.close();
        }
      });

      this.initTriggers();
    }

    initTriggers() {
      if (this.triggerObserver) {
        this.triggerObserver.disconnect();
      }

      this.triggerObserver = new MutationObserver(() => {
        document.querySelectorAll('.quick-view-modal__trigger').forEach(trigger => {
          if (trigger._quickViewInit) return;
          trigger.addEventListener('click', event => this.openProduct(event));
          trigger._quickViewInit = true;
        });
      });

      this.triggerObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;
      element.querySelectorAll('script').forEach(oldScript => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach(attribute => {
          newScript.setAttribute(attribute.name, attribute.value);
        });
        newScript.appendChild(document.createTextNode(oldScript.innerHTML));
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }

    executeScripts(root) {
      root.querySelectorAll('script').forEach(oldScript => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach(attribute => {
          newScript.setAttribute(attribute.name, attribute.value);
        });
        newScript.appendChild(document.createTextNode(oldScript.innerHTML));
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }

    async openProduct(event) {
      event.preventDefault();
      event.stopPropagation();

      const trigger = event.target.closest('.quick-view-modal__trigger');
      if (!trigger || trigger.disabled) return;

      const productCard = trigger.closest('card-product-slider');
      if (productCard?.slider?.autoplay) {
        productCard.slider.autoplay.stop();
        productCard.classList.add('product--open-on-quick-view');
      }

      trigger.classList.add('is--loading');
      this.loading?.removeAttribute('hidden');

      try {
        let fetchPrefix = '';
        if (window.Shopify?.routes?.root?.includes(`/${window.Shopify.locale}/`)) {
          fetchPrefix = `/${window.Shopify.locale}`;
        }

        const response = await fetch(`${fetchPrefix}${trigger.dataset.productUrl}/?view=quick-view`);
        if (!response.ok) return;

        const htmlText = await response.text();
        const parsed = document.createElement('div');
        parsed.insertAdjacentHTML('beforeend', htmlText);

        const productInfo = parsed.querySelector('product-info');
        const productGrid = parsed.querySelector('.product-grid');

        if (!productInfo && !productGrid) return;

        const wrapper = document.createElement('div');
        wrapper.className = 'quick-view-product';
        wrapper.setAttribute('data-quick-view-content', '');

        if (productInfo) {
          wrapper.appendChild(productInfo.cloneNode(true));
        } else {
          wrapper.appendChild(productGrid.cloneNode(true));
        }

        this.main.innerHTML = '';
        this.main.appendChild(wrapper);

        this.executeScripts(this.main);
        window.SortSizeOptions?.scheduleSort(this.main);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.initProductSiblingsCarousels?.(this.main);
          });
        });

        this.open();
      } catch (error) {
        // silent fail
      } finally {
        trigger.classList.remove('is--loading');
        this.loading?.setAttribute('hidden', '');
      }
    }

    initMediaSlider() {
      // Media gallery is initialized by product-media-gallery.js from fetched PDP markup.
    }

    open() {
      this.toggleState = true;
      document.body.classList.add('overflow-hidden');
      document.body.classList.add('quick-view-modal-is-open');
      this.classList.add('is--open');
      this.dialog?.setAttribute('tabindex', '0');
      this.closeButton?.setAttribute('aria-expanded', 'true');

      const focusable = this.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }

    close() {
      this.toggleState = false;
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('quick-view-modal-is-open');
      this.classList.remove('is--open');
      this.closeButton?.setAttribute('aria-expanded', 'false');

      this.main.innerHTML = '';
      this.loading?.setAttribute('hidden', '');

      document.querySelectorAll('[aria-controls="quick-view-modal"]').forEach(button => {
        button.setAttribute('aria-expanded', 'false');
      });

      const sliderCard = document.querySelector('card-product-slider.product--open-on-quick-view');
      if (sliderCard?.slider?.autoplay) {
        sliderCard.slider.autoplay.start();
        sliderCard.classList.remove('product--open-on-quick-view');
      }
    }
  }

  customElements.define('quick-view-modal', QuickViewModal);
}
